import { HelpOutline, Article, Email, Chat } from '@mui/icons-material'
import {
  alpha,
  darken,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material'
import { useState } from 'react'

import ChatbotEmbed from '~/components/chatbotEmbed'
import usePartnerCentralized from '~/features/partners/hooks/usePartnerCentralized'
import rem, { lightenOrDarken } from '~/utils/ui'

function HelpMenu() {
  const theme = useTheme()
  const { partner } = usePartnerCentralized()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const menuItemSx = {
    '& .MuiListItemIcon-root > svg': {
      color: theme.palette.menuIcon,
      transition: 'all 0.3s ease',
    },
    '&:hover .MuiListItemIcon-root > svg': {
      color: darken(theme.palette.menuIcon, 0.4),
    },
    paddingInline: 4,
    paddingBlock: 1,
    '& +.MuiDivider-root': {
      marginBlock: rem(4),
    },
    '& .MuiSvgIcon-root': {
      color: theme.palette.menuFont,
    },
    '&:not(.label):hover': {
      backgroundColor: alpha(theme.palette.mainBackground, 0.2),
    },
    '&.label': {
      color: lightenOrDarken(theme.palette.menuFont),
      letterSpacing: rem(0.7),
      fontSize: rem(14),
      '&:hover': {
        backgroundColor: theme.palette.menuBackground,
        cursor: 'default',
      },
    },
  }

  const contactEmail = !partner.supportEmail
    ? 'help@replify.ai'
    : partner.supportEmail

  const openMenuHandler = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const closeMenuHandler = () => {
    setAnchorEl(null)
  }

  const triggerChatbot = () => {
    // Close the menu first
    closeMenuHandler()

    // Try to trigger the chatbot by finding the existing icon
    const triggerExistingChatbot = () => {
      const chatbotIcon = document.getElementById('chatbot-icon')

      if (chatbotIcon) {
        // Use dispatchEvent to trigger the click instead of calling click() method
        chatbotIcon.dispatchEvent(new Event('click', { bubbles: true }))
        return true
      }

      return false
    }

    // Try immediately first
    if (triggerExistingChatbot()) {
      return
    }

    // If not found, try again after a short delay
    setTimeout(() => {
      if (triggerExistingChatbot()) {
        return
      }

      // If still not found, try to manually trigger the chatbot embed
      if (
        (window as Window & { initChatbotEmbed?: () => void }).initChatbotEmbed
      ) {
        ;(
          window as Window & { initChatbotEmbed?: () => void }
        ).initChatbotEmbed?.()
        // Try again after the embed is initialized
        setTimeout(triggerExistingChatbot, 500)
      }
    }, 100)
  }

  return (
    <>
      <IconButton
        size="large"
        aria-label="help menu"
        aria-controls="menu-help"
        aria-haspopup="true"
        onClick={openMenuHandler}
        sx={{
          color: theme.palette.menuIcon,
        }}
      >
        <HelpOutline fontSize="large" />
      </IconButton>
      <Menu
        id="menu-help"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClick={closeMenuHandler}
        onClose={closeMenuHandler}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              color: theme.palette.menuFont,
              backgroundColor: theme.palette.menuBackground,
              overflow: 'visible',
              overflowY: 'auto',
              filter: `drop-shadow(${rem(0)} ${rem(2)} ${rem(8)} ${
                theme.palette.menuBackground
              })`,
              mt: 5,
            },
          },
        }}
      >
        <MenuItem sx={menuItemSx} className="label">
          Help Center
        </MenuItem>

        <Divider
          sx={{
            backgroundColor: alpha(theme.palette.mainBackground, 0.5),
            marginBlock: rem(4), // Added proper spacing around the divider
          }}
          variant="fullWidth"
        />
        <MenuItem
          component="a"
          href="https://docs.replify.ai"
          target="_blank"
          rel="noopener noreferrer"
          sx={menuItemSx}
        >
          <ListItemIcon>
            <Article fontSize="small" />
          </ListItemIcon>
          Documentation
        </MenuItem>

        <MenuItem component="a" href={`mailto:${contactEmail}`} sx={menuItemSx}>
          <ListItemIcon>
            <Email fontSize="small" />
          </ListItemIcon>
          Contact Us
        </MenuItem>

        <MenuItem onClick={triggerChatbot} sx={menuItemSx}>
          <ListItemIcon>
            <Chat fontSize="small" />
          </ListItemIcon>
          Chat with Libby
        </MenuItem>
      </Menu>

      {/* Include the ChatbotEmbed component to ensure the chatbot is available */}
      <ChatbotEmbed />
    </>
  )
}

export default HelpMenu
